import { StockItemsService } from '@/api/services/stock-items.service';
import { StockMovementsService } from '@/api/services/stock-movements.service';
import { StockRecipesService } from '@/api/services/stock-recipes.service';
import { PermissionType } from '@/enums/PermissionType';
import { StockItemType } from '@/enums/StockItemType';
import {
  STOCK_MOVEMENT_TYPE_LABELS,
  StockMovementType,
} from '@/enums/StockMovementType';
import type { IStockItem } from '@/interfaces/IStockItem';
import { searchCatalogItems } from '../../menu/gpthelp-admin-catalog';
import { activeItemOptions } from '../../menu/gpthelp-admin-menu-local';
import { computeItemPrices } from '../../menu/gpthelp-admin-menu-pricing';
import type { IHostToolDefinition } from '../../gpthelp-host-contract';
import {
  HostToolError,
  type ICartadigitalAdminHostToolDeps,
  type IGpthelpTool,
  type IGpthelpToolGate,
} from '../tool-types';
import { switchedOff } from '../tool-switch';
import {
  getLimitArg,
  getOptionalEnum,
  getOptionalNumber,
  getOptionalNumberArray,
  getOptionalString,
  getPageArg,
  normalizeInstantRange,
  RANGE_END_DESCRIPTION,
} from '../tool-args';
import { runToolApiCall } from '../tool-http';
import { ensurePermissionOrThrow } from '../tool-scope';
import { capList, omitNullish } from '../sanitize';
import { buildMenuToolHelpers } from './menu-tools';
import {
  STOCK_MOVEMENT_TYPES_DESCRIPTION,
  isStockMovementType,
} from './stock-shared';
import {
  buildItemCostRow,
  buildNotInCatalogCostRow,
  countUnknownCosts,
  fitRowsToBudget,
  isConditionalRecipeLine,
  knownStockCost,
  liveRecipeLines,
  recipeLineCost,
  summarizeRecipeLines,
} from './stock-costs';

const ensureStockAccess = (deps: ICartadigitalAdminHostToolDeps) =>
  ensurePermissionOrThrow({
    deps,
    permission: PermissionType.CatalogManage,
    message: 'No tiene permisos para ver el stock',
  });

const STOCK_ITEM_TYPE_DESCRIPTION =
  "Stock item type: 'i' = ingredient (consumed via recipes), 'p' = product (sold/tracked directly). Omit it unless the user explicitly asks for only one of the two: 'stock products' means all stock items.";

const ITEM_COSTS_MAX_ITEMS = 50;
/** Below the widget's 60,000-char tool result cap. */
const ITEM_COSTS_MAX_RESULT_CHARS = 50_000;
/** GET /stock/recipes optionIds cap. */
const RECIPES_MAX_OPTION_IDS = 500;

const stockItemRow = (item: IStockItem) => {
  const quantity = Number(item.quantity);
  const minimumQuantity = Number(item.minimumQuantity);
  return {
    ...omitNullish({
      id: item.id,
      name: item.name,
      type: item.type,
      unit: item.unit,
      quantity,
      minimumQuantity,
      belowMinimum: quantity < minimumQuantity || null,
    }),
    cost: knownStockCost(item.cost),
  };
};

const getPositiveIntArg = (
  args: Record<string, unknown>,
  key: string
): number | null => {
  const raw = getOptionalNumber(args, key);
  if (raw === null) return null;
  const value = Math.floor(raw);
  if (!Number.isFinite(value) || value <= 0) {
    throw new HostToolError('INVALID_ARGS', `Invalid "${key}"`, { [key]: raw });
  }
  return value;
};

const getItemIdsArg = (args: Record<string, unknown>): number[] | null => {
  const raw = getOptionalNumberArray(args, 'itemIds', ITEM_COSTS_MAX_ITEMS);
  if (raw === null) return null;
  const ids = raw.map((id) => Math.floor(id));
  if (ids.some((id) => id <= 0)) {
    throw new HostToolError('INVALID_ARGS', 'Invalid "itemIds"', {
      itemIds: args.itemIds,
    });
  }
  return [...new Set(ids)];
};

/**
 * stock_getRecipes' description. Its closing pointers to stock_getItemCosts
 * and menu_getItem are said only while those tools are not switched off for
 * the member (plan F10 naming rule; both tools are otherwise offered whenever
 * this one is). Without a gate (the static definition) both are said.
 */
export function recipesDescription(
  gate?: Pick<IGpthelpToolGate, 'assistantDisabledTools'>
): string {
  const costs = !gate || !switchedOff('stock_getItemCosts', gate);
  const itemRead = !gate || !switchedOff('menu_getItem', gate);
  const pointers =
    costs && itemRead
      ? ' For many items at once use stock_getItemCosts; resolve option ids with menu_getItem.'
      : costs
      ? ' For many items at once use stock_getItemCosts.'
      : itemRead
      ? ' Resolve option ids with menu_getItem.'
      : '';
  return `${RECIPES_DESCRIPTION}${pointers}`;
}

const RECIPES_DESCRIPTION =
  "Stock recipe lines: how much of each stock item a menu item or an option consumes, at CURRENT stock costs; a null cost, lineCost or unitCost is UNKNOWN (no recipe or stock cost not loaded), never 0. Pass EXACTLY ONE of: itemId (every line of the item; conditional:true lines apply only when the diner picks that option/variant; unitCost sums the unconditional lines and unpricedStock names stock items without a cost), optionId or optionItemId (that option's lines, conditional on the diner's choice), stockItemId (reverse lookup: every line consuming that stock item).";

const RECIPES_DEFINITION: IHostToolDefinition = {
  name: 'stock_getRecipes',
  description: recipesDescription(),
  sideEffects: 'none',
  parameters: {
    type: 'object',
    additionalProperties: false,
    properties: {
      itemId: { type: 'number', description: 'Menu item id.' },
      optionId: { type: 'number', description: 'Option group id.' },
      optionItemId: { type: 'number', description: 'Option choice id.' },
      stockItemId: {
        type: 'number',
        description: 'Stock item id (reverse lookup).',
      },
    },
  },
};

export const stockTools: IGpthelpTool[] = [
  {
    definition: {
      name: 'stock_findItems',
      description:
        "Search or list the branch's stock items (ingredients and products) with current quantity, minimum and unit cost (cost null = not loaded). Set belowMinimumOnly for 'what stock is running low'.",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          search: { type: 'string' },
          type: {
            type: 'string',
            enum: ['i', 'p'],
            description: STOCK_ITEM_TYPE_DESCRIPTION,
          },
          belowMinimumOnly: {
            type: 'boolean',
            default: false,
            description: 'Only items whose quantity is below their minimum.',
          },
          sortBy: {
            type: 'string',
            enum: ['name', 'quantity', 'cost'],
            default: 'name',
          },
          page: { type: 'number', minimum: 1, default: 1 },
          limit: { type: 'number', minimum: 1, maximum: 100, default: 20 },
        },
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canManageCatalog,
    async execute(args, { deps, scope }) {
      ensureStockAccess(deps);

      const search = getOptionalString(args, 'search');
      const type = getOptionalEnum(args, 'type', [
        StockItemType.Ingredient,
        StockItemType.Product,
      ] as const);
      const belowMinimumOnly = args.belowMinimumOnly === true;
      const sortBy =
        getOptionalEnum(args, 'sortBy', ['name', 'quantity', 'cost'] as const) ??
        'name';
      const page = getPageArg(args);
      const limit = getLimitArg(args, { def: 20, max: 100 });

      if (belowMinimumOnly) {
        // A single normal page would silently miss below-minimum items on
        // later pages — fetch the full set (500 cap) and filter client-side.
        const data = await runToolApiCall(() =>
          StockItemsService.getAll({
            restaurantId: scope.restaurantId,
            branchId: scope.branchId,
            type,
            options: {
              page: 1,
              itemsPerPage: 500,
              search: search ?? undefined,
              sortBy: [sortBy],
              sortDesc: [false],
              mustSort: true,
            },
          }),
        );

        const below = (data.items ?? [])
          .map(stockItemRow)
          .filter((row) => row.belowMinimum === true);
        return { totalBelowMinimum: below.length, ...capList(below, limit) };
      }

      const data = await runToolApiCall(() =>
        StockItemsService.getAll({
          restaurantId: scope.restaurantId,
          branchId: scope.branchId,
          type,
          options: {
            page,
            itemsPerPage: limit,
            search: search ?? undefined,
            sortBy: [sortBy],
            sortDesc: [false],
            mustSort: true,
          },
        }),
      );

      return {
        total: data.total,
        page,
        results: (data.items ?? []).map(stockItemRow),
      };
    },
  },
  {
    definition: {
      name: 'stock_getMovements',
      description:
        'Stock movement history (purchases, sales consumption, manual adjustments, damage/theft...). Filter by stock item, movement type or date range to audit stock changes.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          stockItemId: {
            type: 'number',
            description: 'Filter to one stock item (resolve via stock_findItems).',
          },
          movementType: {
            type: 'number',
            description: STOCK_MOVEMENT_TYPES_DESCRIPTION,
          },
          itemType: {
            type: 'string',
            enum: ['i', 'p'],
            description: STOCK_ITEM_TYPE_DESCRIPTION,
          },
          from: {
            type: 'string',
            description: 'Start date (yyyy-MM-dd, branch-local) or ISO instant.',
          },
          to: { type: 'string', description: RANGE_END_DESCRIPTION },
          page: { type: 'number', minimum: 1, default: 1 },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 20 },
        },
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canManageCatalog,
    async execute(args, { deps, scope }) {
      ensureStockAccess(deps);

      const stockItemIdRaw = getOptionalNumber(args, 'stockItemId');
      const stockItemId =
        stockItemIdRaw !== null ? Math.floor(stockItemIdRaw) : undefined;
      const movementTypeRaw = getOptionalNumber(args, 'movementType');
      if (movementTypeRaw !== null && !isStockMovementType(movementTypeRaw)) {
        throw new HostToolError('INVALID_ARGS', 'Invalid "movementType"', {
          movementType: movementTypeRaw,
        });
      }
      const movementType =
        movementTypeRaw !== null ? (movementTypeRaw as StockMovementType) : undefined;
      const itemType = getOptionalEnum(args, 'itemType', [
        StockItemType.Ingredient,
        StockItemType.Product,
      ] as const);
      const page = getPageArg(args);
      const limit = getLimitArg(args, { def: 20, max: 50 });

      const { from, to } = normalizeInstantRange({
        obj: args,
        fromKey: 'from',
        toKey: 'to',
        timeZone: scope.timeZone,
      });

      const data = await runToolApiCall(() =>
        StockMovementsService.getAll({
          restaurantId: scope.restaurantId,
          branchId: scope.branchId,
          itemType: itemType ?? undefined,
          type: movementType,
          stockItemId,
          from: from ?? undefined,
          to: to ?? undefined,
          options: { page, itemsPerPage: limit },
        }),
      );

      const results = (data.items ?? []).map((m) =>
        omitNullish({
          id: m.id,
          stockItem: m.stockItem
            ? {
                id: m.stockItem.id,
                name: m.stockItem.name,
                unit: m.stockItem.unit,
              }
            : { id: m.stockItemId },
          quantity: Number(m.quantity),
          direction: m.direction,
          type: m.type,
          typeLabel: STOCK_MOVEMENT_TYPE_LABELS[m.type]?.label ?? null,
          orderId: m.orderId,
          note: m.note || null,
          createdAt: m.createdAt,
        }),
      );

      return { total: data.total, page, results };
    },
  },
  {
    definition: RECIPES_DEFINITION,
    // Its pointers name only the tools this member is offered (plan F10).
    definitionFor: (gate) => ({
      ...RECIPES_DEFINITION,
      description: recipesDescription(gate),
    }),
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canManageCatalog,
    async execute(args, { deps, scope }) {
      ensureStockAccess(deps);

      const modes = [
        ['itemId', getOptionalNumber(args, 'itemId')],
        ['optionId', getOptionalNumber(args, 'optionId')],
        ['optionItemId', getOptionalNumber(args, 'optionItemId')],
        ['stockItemId', getOptionalNumber(args, 'stockItemId')],
      ] as const;

      const provided = modes.filter(([, v]) => v !== null);
      if (provided.length !== 1) {
        throw new HostToolError(
          'INVALID_ARGS',
          'Pass exactly ONE of itemId, optionId, optionItemId, stockItemId',
          { provided: provided.map(([k]) => k) },
        );
      }

      const [mode, rawId] = provided[0];
      const id = Math.floor(rawId!);
      if (!Number.isFinite(id) || id <= 0) {
        throw new HostToolError('INVALID_ARGS', `Invalid "${mode}"`, { [mode]: rawId });
      }

      const recipes = await runToolApiCall(() => {
        switch (mode) {
          case 'itemId':
            return StockRecipesService.findByItemId(
              scope.restaurantId,
              scope.branchId,
              id,
            );
          case 'optionId':
            return StockRecipesService.findByOptionId(
              scope.restaurantId,
              scope.branchId,
              id,
            );
          case 'optionItemId':
            return StockRecipesService.findByOptionItemId(
              scope.restaurantId,
              scope.branchId,
              id,
            );
          default:
            return StockRecipesService.findByStockItemId(
              scope.restaurantId,
              scope.branchId,
              id,
            );
        }
      });

      const recipeLines = liveRecipeLines(recipes ?? []);
      const lines = recipeLines.map((line) => ({
        ...omitNullish({
          recipeId: line.id,
          itemId: line.itemId,
          optionId: line.optionId,
          optionItemId: line.optionItemId,
          conditional: isConditionalRecipeLine(line) || null,
        }),
        stockItem: {
          id: line.stockItem.id,
          name: line.stockItem.name,
          unit: line.stockItem.unit,
          cost: knownStockCost(line.stockItem.cost),
        },
        quantity: Number(line.quantity),
        lineCost: recipeLineCost(line),
      }));

      if (mode !== 'itemId') return { mode, lines };

      // Conditional (per-option/variant) lines are mutually exclusive per
      // sold unit — summing them would inflate the base food cost.
      const { unitCost, unpricedStock } = summarizeRecipeLines(
        recipeLines.filter((line) => !isConditionalRecipeLine(line))
      );
      return {
        mode,
        unitCost,
        ...(unpricedStock.length > 0 ? { unpricedStock } : {}),
        lines,
      };
    },
  },
  {
    definition: {
      name: 'stock_getItemCosts',
      description:
        'Recipe cost of menu items at CURRENT stock costs next to their price before tax, many items per call. unitCost = what every sale consumes; null = UNKNOWN, never 0: hasRecipe:false = no recipe: estimate from the stock products its name or description names, via stock_findItems (the description is included); unpricedStock = its stock items without a loaded cost. variants = price variants (sizes...), each with its own price, unitCost and margin. optionRecipes:true = chosen extras consume more stock, not in unitCost (detail: stock_getRecipes optionItemId). notInCatalog:true = deleted item still in sales, costed from its recipe. Pass itemIds for specific items, or omit them to page through the catalog. remainingItemIds = rows left out for size: fetch them with itemIds=remainingItemIds before the next page.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          itemIds: {
            type: 'array',
            items: { type: 'number' },
            maxItems: ITEM_COSTS_MAX_ITEMS,
            description:
              'Menu item ids, e.g. the items sold in caja_resume or stats_products_rank.',
          },
          categoryId: {
            type: 'number',
            description: 'Only this category (catalog paging, not with itemIds).',
          },
          page: { type: 'number', minimum: 1, default: 1 },
          limit: {
            type: 'number',
            minimum: 1,
            maximum: ITEM_COSTS_MAX_ITEMS,
            default: ITEM_COSTS_MAX_ITEMS,
          },
        },
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canManageCatalog,
    async execute(args, { deps, scope }) {
      ensureStockAccess(deps);

      const itemIds = getItemIdsArg(args);
      const categoryId = getPositiveIntArg(args, 'categoryId');
      if (itemIds && categoryId !== null) {
        throw new HostToolError(
          'INVALID_ARGS',
          'Pass either "itemIds" or "categoryId", not both',
          { itemIds, categoryId }
        );
      }
      const page = itemIds ? 1 : getPageArg(args);
      const limit = itemIds
        ? itemIds.length
        : getLimitArg(args, {
            def: ITEM_COSTS_MAX_ITEMS,
            max: ITEM_COSTS_MAX_ITEMS,
          });

      const helpers = buildMenuToolHelpers(deps);
      const snapshot = helpers.getMenuCatalogSnapshotOrThrow();
      const search = searchCatalogItems(
        { itemIds, categoryId, limit, offset: (page - 1) * limit },
        {
          categories: snapshot.categories,
          items: snapshot.items,
          availableMenus: deps.menuStore.availableMenus,
          ctx: helpers.visibilityCtx,
          taxCtx: helpers.taxCtx,
        }
      );
      const pageItems = search.results.map((row) => ({
        row,
        item: snapshot.itemsById[row.itemId],
      }));
      const notInCatalogIds = search.missing ?? [];

      if (pageItems.length === 0 && notInCatalogIds.length === 0) {
        return { total: search.total, page, unknownCostOnPage: 0, results: [] };
      }

      const optionIds = [
        ...new Set(
          pageItems.flatMap(({ item }) =>
            activeItemOptions(item)
              .map((option) => option.id)
              .filter((id): id is number => typeof id === 'number')
          )
        ),
      ].sort((a, b) => a - b);
      if (optionIds.length > RECIPES_MAX_OPTION_IDS) {
        throw new HostToolError(
          'INVALID_ARGS',
          'Too many options on this page; request fewer items',
          { optionIds: optionIds.length }
        );
      }

      const recipes = await runToolApiCall(() =>
        StockRecipesService.findForItems(
          scope.restaurantId,
          scope.branchId,
          [...pageItems.map(({ row }) => row.itemId), ...notInCatalogIds],
          optionIds
        )
      );
      const lines = liveRecipeLines(recipes ?? []);

      const rows = [
        ...pageItems.map(({ row, item }) => {
          const prices = computeItemPrices(item, helpers.taxCtx);
          return buildItemCostRow({
            item,
            price: prices.discounted ?? prices.base,
            categoryName: row.categoryName,
            hiddenReasons: row.hiddenReasons,
            lines,
          });
        }),
        ...notInCatalogIds.map((itemId) =>
          buildNotInCatalogCostRow(itemId, lines)
        ),
      ];
      const { kept, remainingItemIds } = fitRowsToBudget(
        rows,
        ITEM_COSTS_MAX_RESULT_CHARS
      );

      return {
        total: itemIds ? rows.length : search.total,
        page,
        unknownCostOnPage: countUnknownCosts(kept),
        results: kept,
        ...(remainingItemIds.length > 0 ? { remainingItemIds } : {}),
      };
    },
  },
];
